import { DateTime } from "luxon";
import { CheckStatusEnum } from "../../context/MandateStatusContext";
import { SEGMENT_LABELS } from "../../utils/screeningMatch";

export const formatDate = (date: any) => {
  if (!date) return "-";
  if (Array.isArray(date)) return date.map(formatDate).join(", ");

  const value = String(date);
  const parts = value.split("-");
  const filteredParts = parts.filter(
    (part) => part !== "00" && part !== "0000",
  );

  if (filteredParts.length !== parts.length) {
    return filteredParts.length ? filteredParts.join("-") : "-";
  }

  const parsedDate = DateTime.fromISO(value);
  return parsedDate.isValid
    ? parsedDate.toFormat("dd/MM/yyyy")
    : filteredParts.join("-");
};

export const formatSourceDate = (date: any) => {
  if (!date) return "-";

  return DateTime.fromISO(date).isValid
    ? DateTime.fromISO(date).toFormat("dd/MM/yyyy")
    : date;
};

export const formatPepDate = (date: unknown) => {
  if (!date) return "-";

  const value = String(date);
  const partialDate = value.match(/^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?$/);
  if (partialDate) {
    const [, year, month, day] = partialDate;
    const parsedDate = DateTime.fromObject({
      year: Number(year),
      month: Number(month),
      day: Number(day || 1),
    });

    return parsedDate.isValid
      ? parsedDate.toFormat(day ? "dd/MM/yyyy" : "MM/yyyy")
      : value;
  }

  return formatDate(value);
};

/**
 * `decision.decided_by` is a user object, `{id, first_name, last_name}`, not a
 * name. Returns the display name, or an empty string where nobody has decided
 * the match yet.
 */
export const decidedByName = (decidedBy: any) => {
  if (!decidedBy) return "";
  if (typeof decidedBy === "string") return decidedBy;
  return [decidedBy.first_name, decidedBy.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
};

/** First letter of first + last name, for the decider's avatar initials. */
export const decidedByInitials = (decidedBy: any) => {
  if (!decidedBy) return "";
  if (typeof decidedBy === "string") {
    return decidedBy
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((word) => word[0])
      .join("")
      .toUpperCase();
  }
  return [decidedBy.first_name, decidedBy.last_name]
    .filter(Boolean)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
};

/** The host of a source URL, which is what signals whether it is credible. */
export const sourceDomain = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

export const formatLabel = (key: unknown) =>
  String(key ?? "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const getSegmentLabel = (key: string) =>
  SEGMENT_LABELS[key] || formatLabel(key);

export const DECISION_LABELS: Record<string, string> = {
  true_match: "True Match",
  false_positive: "False Positive",
  unknown: "Unknown",
};

/**
 * Colour encodes the risk outcome, not user approval, so that red always means
 * "attention here" when scanning a long list of matches. A confirmed sanctions
 * or PEP hit is the serious state; a false positive is the benign one.
 */
export const DECISION_COLORS: Record<string, { bg: string; color: string }> = {
  true_match: { bg: "red.50", color: "red.40" },
  false_positive: { bg: "green.50", color: "green.primary" },
  unknown: { bg: "gray.10", color: "gray.60" },
};

/** Label colour for the check-level manual accept/reject decision. */
export const CHECK_DECISION_LABEL_COLOR: Record<string, string> = {
  [CheckStatusEnum.ManuallyAccepted]: "green.primary",
  [CheckStatusEnum.ManuallyRejected]: "red.40",
};
export const renderValue = (value: any) => {
  if (value === null || value === undefined || value === "") return "-";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

export const formatIds = (ids?: any[]) =>
  (ids || [])
    .filter((idEntry) => idEntry?.number)
    .map((idEntry) =>
      idEntry?.type ? `${idEntry.number} (${idEntry.type})` : idEntry.number,
    );

export const formatAddresses = (addresses?: any[]) =>
  (addresses || []).map((address) => {
    const line = [address?.street1, address?.street2]
      .filter(Boolean)
      .join(", ");
    return address?.category ? `${line} (${address.category})` : line;
  });
